import { create } from 'zustand';
import { supabase, isSupabaseConfigured } from '@/lib/supabase';
import { startSupabaseSync, stopSupabaseSync } from '@/lib/sync';

export interface AuthUser {
  id: string;
  email: string;
  displayName: string | null;
  avatarUrl: string | null;
  isAdmin: boolean;
}

interface AuthState {
  user: AuthUser | null;
  accessToken: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  init: () => Promise<void>;
  signOut: () => Promise<void>;
}

function toUser(u: any): AuthUser {
  return {
    id: u.id,
    email: u.email ?? '',
    displayName: (u.user_metadata?.display_name as string) ?? null,
    avatarUrl: (u.user_metadata?.avatar_url as string) ?? null,
    isAdmin: Boolean(u.app_metadata?.is_admin),
  };
}

/**
 * Only apply the new user object if any field actually changed. Supabase fires
 * `onAuthStateChange` multiple times per session (INITIAL_SESSION, SIGNED_IN,
 * TOKEN_REFRESHED, …) with fresh objects each call. Without this guard every
 * fire would create a new user object and re-render every useAuthStore
 * consumer, looping under StrictMode.
 */
function sameUser(a: AuthUser | null, b: AuthUser | null) {
  if (a === b) return true;
  if (!a || !b) return false;
  return a.id === b.id && a.email === b.email && a.displayName === b.displayName
    && a.avatarUrl === b.avatarUrl && a.isAdmin === b.isAdmin;
}

let initStarted = false;

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  accessToken: null,
  isLoading: true,
  isAuthenticated: false,

  async init() {
    // Run once per tab, even under StrictMode double-mount.
    if (initStarted) return;
    initStarted = true;

    if (!isSupabaseConfigured) {
      set({ isLoading: false });
      return;
    }

    function applySession(session: any, opts: { startSync?: boolean } = {}) {
      if (session?.user) {
        const user = toUser(session.user);
        const prev = get().user;
        if (!sameUser(prev, user) || get().accessToken !== session.access_token) {
          set({ user, accessToken: session.access_token, isAuthenticated: true });
        }
        if (opts.startSync) void startSupabaseSync(user.id);
      } else {
        if (get().user) {
          stopSupabaseSync();
          set({ user: null, accessToken: null, isAuthenticated: false });
        }
      }
    }

    const { data } = await supabase.auth.getSession();
    applySession(data.session, { startSync: true });
    set({ isLoading: false });

    supabase.auth.onAuthStateChange((event, session) => {
      applySession(session, { startSync: event === 'SIGNED_IN' || event === 'INITIAL_SESSION' });
    });
  },

  async signOut() {
    stopSupabaseSync();
    if (isSupabaseConfigured) await supabase.auth.signOut();
    set({ user: null, accessToken: null, isAuthenticated: false });
  },
}));
